import { FC, useState, useMemo } from "react";

import { Modal, Space, Button, Input, Form, Radio, Empty } from "antd";

import Avatar from "@/components/Avatar";

import { tier_temporary } from "@/components/pages/search/search-profiles";

import { useRequestChampion, useGetConnections } from "@/hooks/useConnections";

import type { RadioChangeEvent } from "antd";
import CardUserProfile from "@/components/Card/card-user-profile";
import getUserStatus from "@/utils/string/getUserStatus";

interface IModalRequestCircleChampion {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: any;
  onError?: any;
  totalChampionLeft: number;
}

const ModalRequestCircleChampion: FC<IModalRequestCircleChampion> = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  totalChampionLeft,
}) => {
  const [isProcess, setIsProcess] = useState<boolean>(false);

  const [filterKeywordChampion, setFilterKeywordChampion] = useState<string>("");

  const [selectedUserChampion, setSelectedUserChampion] = useState<{
    data: Record<string, any> | null;
    isConfirm: boolean;
  }>({
    data: null,
    isConfirm: false,
  });

  const { data, isFetching } = useGetConnections({});

  const dataUsers = useMemo(() => {
    const result: any[] = data?.data?.filter((item: any) => !item?.isChampion) as any[];

    const filteredUsers = result?.filter((item: any) => {
      const name = `${item?.user?.firstName} ${item?.user?.lastName}`;

      if (
        filterKeywordChampion &&
        !name.toLowerCase().includes(filterKeywordChampion.toLowerCase())
      ) {
        return false;
      }

      return true;
    });

    const sortedUsers = filteredUsers?.sort((a, b) => {
      const aFullName = `${a?.user?.firstName}${a?.user?.lastName}`;
      const bFullName = `${b?.user?.firstName}${b?.user?.lastName}`;
      return aFullName.localeCompare(bFullName);
    });

    return sortedUsers;
  }, [data, filterKeywordChampion]);

  const handleRequestCircleChampion = async (values: any) => {
    setIsProcess(true);

    try {
      await useRequestChampion(selectedUserChampion?.data?.user?.id, values?.message);

      onClose();
      setSelectedUserChampion({
        data: null,
        isConfirm: false,
      });

      if (onSuccess) {
        onSuccess({
          title: `Invitation Sent (Circle of Champions)`,
          // description: `You successfully request to ${selectedUserChampion?.data?.user?.firstName} ${selectedUserChampion?.data?.user?.lastName} has been sent`,
          description: `You have successfully sent an invitation to ${selectedUserChampion?.data?.user?.firstName} ${selectedUserChampion?.data?.user?.lastName} to become one of your featured Champions. `,
        });
      }

      setSelectedUserChampion({
        data: null,
        isConfirm: false,
      });
    } catch (error: any) {
      if (onError) {
        onError({
          title: `Request circle champion failed`,
          description:
            error?.errors?.[0]?.message || "Request failed send request, please try again",
        });
      }
    } finally {
      setIsProcess(false);
    }
  };

  return (
    <Modal footer={null} open={isOpen} onCancel={onClose} title="Request Circle of Champion">
      {selectedUserChampion.isConfirm && selectedUserChampion.data ? (
        <div className="py-8 px-6">
          <div className="block text-center text-white text-base mb-4">
            Would you like to reward{" "}
            {selectedUserChampion?.data?.user?.firstName ||
              selectedUserChampion?.data?.user?.lastName}{" "}
            by honoring them as one of your Champions at your organization?
          </div>
          <div className="flex w-full justify-center mb-5">
            <CardUserProfile
              avatarOnly
              avatarSize="lg"
              item={selectedUserChampion?.data?.user}
              points={selectedUserChampion?.data?.user?.points}
              cgPercent={selectedUserChampion?.data?.user?.CGPercent}
            />
          </div>
          <div className="block text-center text-white text-base mb-4">
            <p className="font-body text-gray-300 text-xs mb-1">
              {selectedUserChampion?.data?.user?.jobTitle}
            </p>
            <h4 className="font-bold text-lg font-body text-gray-100 mb-1">
              {`${selectedUserChampion?.data?.user?.firstName} ${selectedUserChampion?.data?.user?.lastName}`}
            </h4>
            {selectedUserChampion?.data?.user?.lookingForType && (
              <p className="font-body text-gray-200 text-xs">
                {getUserStatus(
                  selectedUserChampion?.data?.user?.type,
                  selectedUserChampion?.data?.user?.lookingForType,
                )}
              </p>
            )}
          </div>

          <Form onFinish={handleRequestCircleChampion} layout="vertical">
            <Form.Item
              name="message"
              label={`Write an introduction`}
              rules={[{ required: true, message: "Please write an introduction to continue." }]}
            >
              <Input.TextArea
                allowClear
                rows={5}
                placeholder="Write an introduction here…"
                style={{ resize: "none" }}
                showCount
                maxLength={500}
              />
            </Form.Item>

            <div className="flex justify-center mt-10">
              <Space>
                <Button size="large" disabled={isProcess} onClick={() => onClose()}>
                  Decline
                </Button>
                <Button type="primary" size="large" htmlType="submit" loading={isProcess}>
                  Add to Circle of Champions
                </Button>
              </Space>
            </div>
          </Form>
        </div>
      ) : (
        <div>
          <div className="mb-8 mt-4">
            <Input.Search
              placeholder="search people"
              allowClear
              enterButton
              onReset={() => setSelectedUserChampion((prev) => ({ ...prev, user: null }))}
              onSearch={(value) => setFilterKeywordChampion(value)}
            />
          </div>

          {isFetching ? (
            <div className="grid gird-cols-1 gap-4">
              <div className="animate-pulse">
                <div className="w-full h-20 bg-neutral-700 rounded-lg "></div>
              </div>
              <div className="animate-pulse">
                <div className="w-full h-20 bg-neutral-700 rounded-lg "></div>
              </div>
            </div>
          ) : dataUsers?.length > 0 ? (
            <div className="-mx-6 px-6 overflow-auto max-h-96">
              <Radio.Group
                onChange={(e: RadioChangeEvent) =>
                  setSelectedUserChampion((prev) => ({ ...prev, data: e.target.value }))
                }
                className="block w-full"
                value={selectedUserChampion.data}
              >
                <div className="grid grid-col-1 gap-4">
                  {dataUsers?.map((item: any, idx: number) => (
                    <div
                      className="flex items-center justify-between gap-4 w-full mb-4 pb-6 border-b border-neutral-400 last:border-b-0"
                      key={`item-of-mutual-${idx}`}
                    >
                      <div className="flex gap-3 items-center w-full">
                        <Avatar
                          src={item?.user?.avatarUrl}
                          size="sm"
                          alt="profile"
                          type_tier={tier_temporary(item?.user?.points)}
                          badge={item?.CGPercent || null}
                          name={item?.user?.bio?.firstName}
                        />
                        <div className="flex-1 block ">
                          <p className="font-body text-gray-200 text-sm">{item?.user?.jobTitle}</p>
                          <p className="text-base">
                            <strong>{`${item?.user?.firstName} ${item?.user?.lastName}`}</strong>
                          </p>
                          {item?.user?.lookingForType && (
                            <p className="font-body text-gray-200 text-xs">
                              {getUserStatus(item?.user?.type, item?.user?.lookingForType)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <Radio value={item} />
                      </div>
                    </div>
                  ))}
                </div>
              </Radio.Group>
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}

          <div className="flex w-full items-center justify-between gap-4 py-4">
            <div className="text-white font-semibold">{totalChampionLeft} Invitations left</div>
            <Space>
              <Button onClick={() => onClose()}>Dismiss</Button>
              <Button
                type="primary"
                disabled={selectedUserChampion.data === null}
                onClick={() => setSelectedUserChampion((prev) => ({ ...prev, isConfirm: true }))}
              >
                Next
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ModalRequestCircleChampion;
